import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TfiEmail } from "react-icons/tfi";
import { FaHome, FaKeyboard, FaBars, FaTimes, FaCoins } from "react-icons/fa";
import { HiOutlineBookOpen } from "react-icons/hi";
import { AiOutlineLineChart, AiOutlineUser } from "react-icons/ai";
import { MdOndemandVideo } from "react-icons/md";
import { ImBlogger } from "react-icons/im";
import { BsFillMenuButtonWideFill } from "react-icons/bs";
import { RiShieldCheckLine } from "react-icons/ri";
import SearchBar from "../Searchbar/Searchbar";
import googlePlay from "../../assets/google.svg";
import logo from "../../assets/pinnacleWhiteLogo.svg";
import { useAuth } from "../../context/AuthContext";

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

  const { isLoggedIn, logout, user } = useAuth();
  const navigate = useNavigate();

  const profileRef = useRef(null);
  const menuButtonRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);

  /** 🔑 CORE FIX — LOGIN WITH RETURN URL */
  const redirectToLoginWithReturn = () => {
    const returnUrl = window.location.href; // FULL typing URL
    window.location.assign(
      "https://testportal.ssccglpinnacle.com/login?redirect=" +
        encodeURIComponent(returnUrl)
    );
  };

  /* Close profile dropdown on outside click */
  useEffect(() => {
    const handleClickOutsideProfile = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideProfile);
    return () =>
      document.removeEventListener("mousedown", handleClickOutsideProfile);
  }, []);

  /* Close mobile menu on outside click */
  useEffect(() => {
    const onDown = (e) => {
      if (!isMobileMenuOpen) return;
      const menu = mobileMenuRef.current;
      const btn = menuButtonRef.current;
      if (!menu || !btn) return;
      if (!menu.contains(e.target) && !btn.contains(e.target)) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [isMobileMenuOpen]);

  const isExternal = (url) => /^https?:\/\//i.test(url);

  const navItems = [
    { path: "/", icon: <FaHome />, label: "Home" },
    {
      path: "https://testportal.ssccglpinnacle.com/",
      icon: <AiOutlineLineChart />,
      label: "Test Portal",
      target: "_blank",
    },
    { path: "/typing", icon: <FaKeyboard />, label: "Typing" },
    {
      path: "https://books.ssccglpinnacle.com/",
      icon: <HiOutlineBookOpen />,
      label: "Books",
      target: "_blank",
    },
    {
      path: "https://videos.ssccglpinnacle.com",
      icon: <MdOndemandVideo />,
      label: "Videos",
      target: "_blank",
    },
    {
      path: "https://ebooks.ssccglpinnacle.com",
      icon: <BsFillMenuButtonWideFill />,
      label: "E-Books",
      target: "_blank",
    },
    {
      path: "https://blogs.ssccglpinnacle.com",
      icon: <ImBlogger />,
      label: "Blogs",
      target: "_blank",
    },
    {
      path: "https://piracy.ssccglpinnacle.com/",
      icon: <RiShieldCheckLine />,
      label: "Piracy",
      target: "_blank",
    },
    {
      path: "https://ssccglpinnacle.com/coins",
      icon: <FaCoins />,
      label: "Coins",
      target: "_blank",
    },
    {
      path: "https://referral.ssccglpinnacle.com/",
      icon: <FaCoins />,
      label: "Refer&Earn",
      target: "_blank",
    },
    isLoggedIn
      ? { type: "profile" }
      : {
          type: "login",
          icon: <AiOutlineUser />,
          label: "Login",
        },
  ];

  return (
    <header className="bg-gray-900 text-white">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 md:px-8">
        <div className="flex items-center gap-4">
          <button
            ref={menuButtonRef}
            onClick={toggleMobileMenu}
            className="md:hidden"
          >
            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>

          <img
            src={logo}
            alt="Logo"
            onClick={() =>
              window.open("https://ssccglpinnacle.com", "_blank")
            }
            className="h-6 cursor-pointer"
          />

          <div className="hidden md:flex items-center gap-2 pl-3">
            <TfiEmail />
            <a
              href="mailto:support@ssccglpinnacle.com"
              className="text-sm hover:text-gray-300"
            >
              support@ssccglpinnacle.com
            </a>
          </div>
        </div>

        <div className="hidden md:flex w-1/3 justify-center">
          <SearchBar />
        </div>

        <a
          href="https://play.google.com/store/apps/details?id=com.ssccgl.pinnacle.testportal"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={googlePlay} alt="Google Play" className="h-8" />
        </a>
      </div>

      {/* Desktop nav */}
      <nav className="hidden md:block bg-gray-900">
        <div className="flex justify-between px-6 py-2 items-center gap-4">
          {navItems.map((item) =>
            item.type === "profile" ? (
              <div key="profile" className="relative" ref={profileRef}>
                <button
                  onClick={() => setIsProfileMenuOpen((p) => !p)}
                  className="w-7 h-7 rounded-full bg-indigo-600 flex items-center justify-center"
                >
                  <AiOutlineUser />
                </button>

                {isProfileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white text-black rounded-lg shadow-lg">
                    <div className="px-4 py-3 border-b">
                      <p className="font-semibold">
                        {user?.full_name || "User"}
                      </p>
                      <p className="text-xs">{user?.email_id}</p>
                    </div>
                    <button
                      onClick={logout}
                      className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : item.type === "login" ? (
              <button
                key="login"
                onClick={redirectToLoginWithReturn}
                className="flex items-center gap-2 hover:text-indigo-400"
              >
                {item.icon}
                {item.label}
              </button>
            ) : isExternal(item.path) ? (
              <a
                key={item.label}
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-indigo-400"
              >
                {item.icon}
                {item.label}
              </a>
            ) : (
              <button
                key={item.label}
                onClick={() => navigate(item.path)}
                className="flex items-center gap-2 hover:text-indigo-400"
              >
                {item.icon}
                {item.label}
              </button>
            )
          )}
        </div>
      </nav>

      {/* Mobile nav */}
      {isMobileMenuOpen && (
        <nav
          ref={mobileMenuRef}
          className="md:hidden bg-gray-800 px-4 py-4 space-y-4"
        >
          {navItems.map((item) =>
            item.type === "profile" ? null : item.type === "login" ? (
              <button
                key="login"
                onClick={redirectToLoginWithReturn}
                className="flex items-center gap-2"
              >
                {item.icon}
                {item.label}
              </button>
            ) : isExternal(item.path) ? (
              <a
                key={item.label}
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                {item.icon}
                {item.label}
              </a>
            ) : (
              <button
                key={item.label}
                onClick={() => {
                  navigate(item.path);
                  setIsMobileMenuOpen(false);
                }}
                className="flex items-center gap-2 w-full text-left"
              >
                {item.icon}
                {item.label}
              </button>
            )
          )}
        </nav>
      )}
    </header>
  );
};

export default Header;



// import React, { useState, useRef, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import { TfiEmail } from "react-icons/tfi";
// import { FaHome, FaKeyboard, FaBars, FaTimes, FaCoins } from "react-icons/fa";
// import { HiOutlineBookOpen } from "react-icons/hi";
// import { AiOutlineLineChart, AiOutlineUser } from "react-icons/ai";
// import { MdOndemandVideo } from "react-icons/md";
// import { ImBlogger } from "react-icons/im";
// import { BsFillMenuButtonWideFill } from "react-icons/bs";
// import { RiShieldCheckLine } from "react-icons/ri";
// import SearchBar from "../Searchbar/Searchbar";
// import googlePlay from "../../assets/google.svg";
// import logo from "../../assets/pinnacleWhiteLogo.svg";
// import { useAuth } from "../../context/AuthContext";

// const Header = () => {
//   const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
//   const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

//   const { isLoggedIn, logout, user } = useAuth();
//   const navigate = useNavigate();

//   // Refs for proper outside-click handling
//   const profileRef = useRef(null);
//   const menuButtonRef = useRef(null);
//   const mobileMenuRef = useRef(null);

//   const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);

//   // Close profile dropdown on outside click
//   useEffect(() => {
//     const handleClickOutsideProfile = (event) => {
//       if (profileRef.current && !profileRef.current.contains(event.target)) {
//         setIsProfileMenuOpen(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutsideProfile);
//     return () => document.removeEventListener("mousedown", handleClickOutsideProfile);
//   }, []);

//   // Close mobile menu only when clicking outside of it (not on the toggle button)
//   useEffect(() => {
//     const onDown = (e) => {
//       if (!isMobileMenuOpen) return;
//       const menu = mobileMenuRef.current;
//       const btn = menuButtonRef.current;
//       if (!menu || !btn) return;
//       const clickedOutsideMenu = !menu.contains(e.target);
//       const clickedMenuButton = btn.contains(e.target);
//       if (clickedOutsideMenu && !clickedMenuButton) {
//         setIsMobileMenuOpen(false);
//       }
//     };
//     document.addEventListener("mousedown", onDown);
//     return () => document.removeEventListener("mousedown", onDown);
//   }, [isMobileMenuOpen]);

//   const isExternal = (url) => /^https?:\/\//i.test(url);

//   // Uniform nav items (no JSX in label, every item has a path)
//   const navItems = [
//     { path: "/", icon: <FaHome />, label: "Home" }, // internal
//     {
//       path: "https://testportal.ssccglpinnacle.com/",
//       icon: <AiOutlineLineChart />,
//       label: "Test Portal",
//       target: "_blank",
//     },
//     { path: "/typing", icon: <FaKeyboard />, label: "Typing" }, // internal route
//     {
//       path: "https://books.ssccglpinnacle.com/",
//       icon: <HiOutlineBookOpen />,
//       label: "Books",
//       target: "_blank",
//     },
//     {
//       path: "https://videos.ssccglpinnacle.com",
//       icon: <MdOndemandVideo />,
//       label: "Videos",
//       target: "_blank",
//     },
//     {
//       path: "https://ebooks.ssccglpinnacle.com",
//       icon: <BsFillMenuButtonWideFill />,
//       label: "E-Books",
//       target: "_blank",
//     },
//     {
//       path: "https://blogs.ssccglpinnacle.com",
//       icon: <ImBlogger />,
//       label: "Blogs",
//       target: "_blank",
//     },
//     {
//       path: "https://piracy.ssccglpinnacle.com/",
//       icon: <RiShieldCheckLine />,
//       label: "Piracy",
//       target: "_blank",
//     },
//     {
//       path: "https://ssccglpinnacle.com/coins",
//       icon: <FaCoins />,
//       label: "Coins",
//       target: "_blank",
//     },
//     {
//       path: "https://referral.ssccglpinnacle.com/",
//       icon: <FaCoins />,
//       label: "Refer&Earn",
//       target: "_blank",
//     },
//     isLoggedIn
//       ? { type: "profile" }
//       : {
//           path: "https://testportal.ssccglpinnacle.com/login",
//           icon: <AiOutlineUser />,
//           label: "Login",
//           target: "_blank",
//         },
//   ];

//   return (
//     <header className="bg-gray-900 text-white">
//       {/* Top bar */}
//       <div className="flex items-center justify-between px-4 py-3 md:px-8">
//         <div className="flex items-center gap-4">
//           <button ref={menuButtonRef} onClick={toggleMobileMenu} className="md:hidden">
//             {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
//           </button>
//           <img
//             src={logo}
//             alt="Logo"
//             onClick={() => window.open("https://ssccglpinnacle.com", "_blank")}
//             className="h-6 w-auto cursor-pointer"
//           />
//           <div className="hidden md:flex items-center gap-2 pl-3">
//             <TfiEmail />
//             <a
//               href="mailto:support@ssccglpinnacle.com"
//               className="text-sm hover:text-gray-300"
//             >
//               support@ssccglpinnacle.com
//             </a>
//           </div>
//         </div>

//         <div className="hidden md:flex w-1/3 justify-center">
//           <SearchBar />
//         </div>

//         <div className="flex items-center gap-4 pr-2 md:pr-0">
//           <a
//             href="https://play.google.com/store/apps/details?id=com.ssccgl.pinnacle.testportal"
//             target="_blank"
//             rel="noopener noreferrer"
//           >
//             <img src={googlePlay} alt="Google Play" className="h-8 w-auto" />
//           </a>
//         </div>
//       </div>

//       {/* Desktop nav */}
//       <nav className="hidden md:block bg-gray-900 text-white">
//         <div className="w-full max-w-8xl flex justify-between px-2 lg:px-8 xl:px-12 py-2 items-center gap-4">
//           {navItems.map((item) =>
//             item.type === "profile" ? (
//               <div className="relative" key="profile" ref={profileRef}>
//                 <button
//                   onClick={() => setIsProfileMenuOpen((prev) => !prev)}
//                   className="flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 transition shadow"
//                 >
//                   <AiOutlineUser className="w-4 h-4 text-white" />
//                 </button>

//                 {isProfileMenuOpen && (
//                   <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl z-50 border border-gray-100 overflow-hidden">
//                     <div className="px-4 py-3 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200">
//                       <p className="font-semibold text-sm text-gray-900">
//                         {user?.full_name || "User"}
//                       </p>
//                       <p className="text-xs text-gray-600 truncate">
//                         {user?.email_id || "No email"}
//                       </p>
//                       <p className="text-xs text-gray-500">
//                         {user?.mobile_number || "No mobile"}
//                       </p>
//                     </div>

//                     <button
//                       onClick={logout}
//                       className="flex items-center gap-2 w-full px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors duration-200"
//                     >
//                       <svg
//                         xmlns="http://www.w3.org/2000/svg"
//                         className="h-4 w-4"
//                         fill="none"
//                         viewBox="0 0 24 24"
//                         stroke="currentColor"
//                       >
//                         <path
//                           strokeLinecap="round"
//                           strokeLinejoin="round"
//                           strokeWidth={2}
//                           d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1m0-10V5"
//                         />
//                       </svg>
//                       Logout
//                     </button>
//                   </div>
//                 )}
//               </div>
//             ) : isExternal(item.path) ? (
//               <a
//                 key={item.label}
//                 href={item.path}
//                 target={item.target === "_blank" ? "_blank" : "_self"}
//                 rel={item.target === "_blank" ? "noopener noreferrer" : undefined}
//                 className="flex items-center gap-2 text-sm font-medium hover:text-indigo-400 transition"
//               >
//                 {item.icon}
//                 <span>{item.label}</span>
//               </a>
//             ) : (
//               <button
//                 key={item.label}
//                 type="button"
//                 onClick={() => navigate(item.path)}
//                 className="flex items-center gap-2 text-sm font-medium hover:text-indigo-400 transition"
//               >
//                 {item.icon}
//                 <span>{item.label}</span>
//               </button>
//             )
//           )}
//         </div>
//       </nav>

//       {/* Mobile nav */}
//       {isMobileMenuOpen && (
//         <nav
//           ref={mobileMenuRef}
//           className="md:hidden bg-gray-800 px-4 py-4 space-y-4"
//         >
//           {navItems.map((item) =>
//             item.type === "profile" ? null : isExternal(item.path) ? (
//               <a
//                 key={item.label}
//                 href={item.path}
//                 target={item.target === "_blank" ? "_blank" : "_self"}
//                 rel={item.target === "_blank" ? "noopener noreferrer" : undefined}
//                 className="flex items-center space-x-2 text-sm hover:text-gray-300"
//                 onClick={() => {
//                   // Close drawer only for same-tab nav; new tab stays open
//                   if (item.target !== "_blank") setIsMobileMenuOpen(false);
//                 }}
//               >
//                 {item.icon}
//                 <span>{item.label}</span>
//               </a>
//             ) : (
//               <button
//                 key={item.label}
//                 type="button"
//                 className="flex items-center space-x-2 text-sm hover:text-gray-300 w-full text-left"
//                 onClick={() => {
//                   navigate(item.path);
//                   setIsMobileMenuOpen(false);
//                 }}
//               >
//                 {item.icon}
//                 <span>{item.label}</span>
//               </button>
//             )
//           )}
//         </nav>
//       )}
//     </header>
//   );
// };

// export default Header;